// API Configuration - loaded from public/config.json
let API_BASE_URL = null;
let configLoadPromise = null;

const getDefaultBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api`;
  }
  return '/api';
};

// Load configuration from public/config.json
export const loadConfig = async () => {
  if (API_BASE_URL) {
    return API_BASE_URL; // Already loaded
  }
  
  if (!configLoadPromise) {
    configLoadPromise = fetch('/config.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to load configuration');
        }
        return response.json();
      })
      .then(config => {
        API_BASE_URL = config.apiBaseUrl;
        return API_BASE_URL;
      })
      .catch(error => {
        console.error('Error loading config:', error);
        // Fallback to app origin based API path
        API_BASE_URL = getDefaultBaseUrl();
        return API_BASE_URL;
      });
  }
  
  return configLoadPromise;
};

// Helper function to get auth token
export const getAuthToken = () => {
  return localStorage.getItem('accessToken');
};

// Helper function to get refresh token
export const getRefreshToken = () => {
  return localStorage.getItem('refreshToken');
};

// Helper function to set auth tokens
export const setAuthTokens = (accessToken, refreshToken) => {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
};

// Helper function to clear auth tokens
export const clearAuthTokens = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
};

// Helper function to get user from localStorage
export const getStoredUser = () => {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

// Helper to check session expiry based on user.expiresAt
export const isSessionExpired = (user = getStoredUser()) => {
  if (!user?.expiresAt) {
    return false;
  }
  return new Date(user.expiresAt).getTime() <= Date.now();
};

// Helper function to store user
export const storeUser = (user) => {
  localStorage.setItem('user', JSON.stringify(user));
};

// Base fetch function with error handling
const apiFetch = async (endpoint, options = {}) => {
  // Ensure config is loaded before making API calls
  const baseUrl = await loadConfig();
  const url = `${baseUrl}${endpoint}`;
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'text/plain, application/json',
  };

  // Add authorization header if token exists
  const token = getAuthToken();
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  // Debug logging
  console.log('API Request:', {
    method: config.method || 'GET',
    url: url,
    headers: config.headers,
    body: options.body
  });

  try {
    const response = await fetch(url, config);
    
    // Parse response
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
      // Try to parse as JSON if possible
      try {
        data = JSON.parse(data);
      } catch (e) {
        // If not JSON, wrap in standard format
        data = { success: response.ok, data: data };
      }
    }

    // Handle HTTP errors
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthTokens();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth:session-expired', {
            detail: { reason: 'unauthorized' }
          }));
        }
      }

      console.error('API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        url: url,
        data: data
      });
      
      // Extract error message from various possible formats
      let errorMessage = 'An error occurred';
      if (typeof data === 'string') {
        errorMessage = data;
      } else if (data.message) {
        errorMessage = data.message;
      } else if (data.errors) {
        errorMessage = typeof data.errors === 'string' ? data.errors : JSON.stringify(data.errors);
      } else if (data.title) {
        errorMessage = data.title;
      }
      
      const error = new Error(errorMessage);
      error.status = response.status;
      error.statusText = response.statusText;
      error.errors = data.errors;
      error.data = data;
      throw error;
    }

    return data;
  } catch (error) {
    // Network error or other issues
    if (error.status) {
      throw error; // Re-throw API errors
    }
    
    const networkError = new Error('Network error. Please check your connection.');
    networkError.status = 0;
    networkError.originalError = error.message;
    throw networkError;
  }
};

// API Methods
export const api = {
  // Authentication
  auth: {
    login: async (loginId, password) => {
      console.log('Login request:', { loginId, password: '***' });
      
      const response = await apiFetch('/Auth/login', {
        method: 'POST',
        body: JSON.stringify({ loginId, password }),
      });
      
      console.log('Login response:', response);
      
      // Store tokens and user data if login successful
      if (response.success && response.data) {
        setAuthTokens(response.data.accessToken, response.data.refreshToken);
        const sessionUser = {
          ...response.data.user,
          role: response.data.user.role?.toLowerCase?.() || response.data.user.role,
          name: response.data.user.fullName,
          store: response.data.user.storeCode || null,
          expiresAt: response.data.expiresAt,
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
        };
        storeUser(sessionUser);
      }
      
      return response;
    },
    
    logout: async () => {
      // Call logout API if needed
      // await apiFetch('/Auth/logout', { method: 'POST' });
      
      // Clear local storage
      clearAuthTokens();
    },
    
    refreshToken: async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await apiFetch('/Auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
      
      if (response.success && response.data) {
        setAuthTokens(response.data.accessToken, response.data.refreshToken);
      }
      
      return response;
    },

    getUsers: async () => {
      return await apiFetch('/Auth/users');
    },

    createUser: async (userData) => {
      return await apiFetch('/Auth/create-user', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
    },

    updateUser: async (id, userData) => {
      return await apiFetch(`/Auth/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      });
    },

    deleteUser: async (id) => {
      return await apiFetch(`/Auth/users/${id}`, {
        method: 'DELETE',
      });
    },
  },

  // Stores
  stores: {
    getAll: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return await apiFetch(`/Stores${queryString ? '?' + queryString : ''}`);
    },
    
    getById: async (id) => {
      return await apiFetch(`/Stores/${id}`);
    },
    
    create: async (storeData) => {
      return await apiFetch('/Stores', {
        method: 'POST',
        body: JSON.stringify(storeData),
      });
    },
    
    update: async (id, storeData) => {
      return await apiFetch(`/Stores/${id}`, {
        method: 'PUT',
        body: JSON.stringify(storeData),
      });
    },
    
    delete: async (id) => {
      return await apiFetch(`/Stores/${id}`, {
        method: 'DELETE',
      });
    },
  },

  // Gold Rates
  goldRates: {
    getAll: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return await apiFetch(`/GoldRates${queryString ? '?' + queryString : ''}`);
    },
    
    getLatest: async (storeCode) => {
      return await apiFetch(`/GoldRates/latest?storeCode=${encodeURIComponent(storeCode || '')}`);
    },
    
    create: async (rateData) => {
      return await apiFetch('/GoldRates', {
        method: 'POST',
        body: JSON.stringify(rateData),
      });
    },
  },

  // Advance Collections
  advances: {
    getAll: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return await apiFetch(`/AdvanceCollections${queryString ? '?' + queryString : ''}`);
    },
    
    getById: async (id) => {
      return await apiFetch(`/AdvanceCollections/${id}`);
    },
    
    create: async (advanceData) => {
      return await apiFetch('/AdvanceCollections', {
        method: 'POST',
        body: JSON.stringify(advanceData),
      });
    },
    
    update: async (id, advanceData) => {
      return await apiFetch(`/AdvanceCollections/${id}`, {
        method: 'PUT',
        body: JSON.stringify(advanceData),
      });
    },
    
    delete: async (id) => {
      return await apiFetch(`/AdvanceCollections/${id}`, {
        method: 'DELETE',
      });
    },
  },

  // Reports
  reports: {
    advanceSummary: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return await apiFetch(`/Reports/advance-summary${queryString ? '?' + queryString : ''}`);
    },
    
    paymodeBreakdown: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return await apiFetch(`/Reports/paymode-breakdown${queryString ? '?' + queryString : ''}`);
    },
  },

  // Dashboard
  dashboard: {
    getStats: async () => {
      return await apiFetch('/Dashboard/stats');
    },
  },

  // User-Store Mapping
  userStores: {
    getAll: async (queryParams = '') => {
      const url = `/UserStores${queryParams}`;
      return await apiFetch(url);
    },

    create: async (mappingData) => {
      return await apiFetch('/UserStores', {
        method: 'POST',
        body: JSON.stringify(mappingData),
      });
    },
  },
};

export default api;
